import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { ApiError, auth as authApi, type User } from "@/lib/api";

type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; user: User };

interface AuthContextValue {
  state: AuthState;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  const refresh = async () => {
    try {
      const { user } = await authApi.me();
      setState({ status: "authenticated", user });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setState({ status: "anonymous" });
      } else {
        // network / 5xx — treat as anonymous so the user can retry login
        setState({ status: "anonymous" });
      }
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore — clearing the cookie locally is enough
    }
    setState({ status: "anonymous" });
  };

  return (
    <AuthContext.Provider value={{ state, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
